import { createRoute, lazyRouteComponent, Outlet } from '@tanstack/react-router';
import { z } from 'zod/v3';

import { menuLayoutRoute } from 'root/MenuLayout';
import RequireWarehouse from 'root/RequireWarehouse';

import VenueSettingsEnabledRoute from './VenueSettingsEnabled';

const VenueSettingsRequireWarehouse = () => {
  return (
    <RequireWarehouse
      routeWithWarehouseId="/venue-settings/warehouse/$warehouseId"
      routeWithoutWarehouseId="/venue-settings"
    >
      <Outlet />
    </RequireWarehouse>
  );
};

const venueSettingsRootRoute = createRoute({
  getParentRoute: () => menuLayoutRoute,
  id: 'venue-settings-root',
});

const requireWarehouseRoute = createRoute({
  getParentRoute: () => venueSettingsRootRoute,
  path: '/venue-settings',
  component: VenueSettingsRequireWarehouse,
});

const venueSettingsWrapperRoute = createRoute({
  getParentRoute: () => requireWarehouseRoute,
  path: 'warehouse/$warehouseId',
  parseParams: z.object({ warehouseId: z.string().uuid() }).parse,
});

const venueSettingsEnabledRoute = createRoute({
  id: 'venue-settings-enabled',
  getParentRoute: () => venueSettingsWrapperRoute,
  component: VenueSettingsEnabledRoute,
});

const unitOfMeasureSortBySchema = z
  .enum([
    'WEIGHT_PER_UNIT_ASC_NULLS_FIRST',
    'WEIGHT_PER_UNIT_DESC_NULLS_FIRST',
    'MEASUREMENT_SYSTEM_UNIT_FIRST',
  ])
  .optional();

const unitOfMeasureFilterSchema = z.enum(['UNITS', 'KILOGRAMS']).optional();

export type UnitOfMeasureFilter = NonNullable<z.infer<typeof unitOfMeasureFilterSchema>>;

const venueSettingsView = createRoute({
  getParentRoute: () => venueSettingsEnabledRoute,
  id: 'venue-settings-view',
  component: lazyRouteComponent(() => import('./VenueSettingsView')),
});

const adminSettingsView = createRoute({
  getParentRoute: () => venueSettingsView,
  path: '/admin-settings',
  component: lazyRouteComponent(() => import('./AdminSettingsView/AdminSettingsView')),
});

const settingsIndexRoute = createRoute({
  getParentRoute: () => venueSettingsView,
  path: '/',
  beforeLoad: ({ navigate, params }) => {
    navigate({
      to: '/venue-settings/warehouse/$warehouseId/admin-settings',
      params: { warehouseId: params.warehouseId },
      replace: true,
    });
  },
});

export const inventoryUnitOfMeasureView = createRoute({
  getParentRoute: () => venueSettingsView,
  path: '/inventory-unit-of-measure',
  component: lazyRouteComponent(
    () => import('./InventoryUnitOfMeasureView/InventoryUnitOfMeasureView'),
  ),
  validateSearch: z.object({
    q: z.string().optional(),
    sortBy: unitOfMeasureSortBySchema,
    minWeightFilter: z.string().optional(),
    maxWeightFilter: z.string().optional(),
    unitOfMeasureFilter: unitOfMeasureFilterSchema,
    productsWithoutWeightPerUnitFilter: z.boolean().optional(),
  }).parse,
});

export const venueSettingsRouteTree = venueSettingsRootRoute.addChildren({
  requireWarehouseRoute: requireWarehouseRoute.addChildren({
    venueSettingsWrapperRoute: venueSettingsWrapperRoute.addChildren({
      venueSettingsEnabledRoute: venueSettingsEnabledRoute.addChildren({
        venueSettingsView: venueSettingsView.addChildren({
          settingsIndexRoute,
          adminSettingsView,
          inventoryUnitOfMeasureView,
        }),
      }),
    }),
  }),
});
